import { IterableElement } from 'type-fest';
import toast from 'react-hot-toast';
import { TrashIcon, UsersIcon } from '@phosphor-icons/react';

import { Breadcrumb } from '../../../components/Breadcrumb';
import { PageHeader } from '../../../components/PageHeader';
import { Customer, useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { Card } from '../../../components/Card';
import { EmptyState } from '../../../components/empty-state/EmptyState';
import { Pill, type PillColor } from '../../../components/pill/Pill';
import { UserIdentityCell } from '../../../components/user/UserIdentityCell';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { getDisplayError } from '../../../utils/get-display-error';
import { deleteCustomerUserInvite } from '../../customer/models';
import { useTranslation } from '../../../contexts/translation-context';
import { ITableHeader, Table } from 'components/table/Table';
import { PageHeading } from 'components/PageHeading';
import { captureException } from '@sentry/react';

export type CustomerUserInvite = IterableElement<Customer['invitedUsers']>;

/**
 * An invitation is the same row as an account with a different status, so both
 * live in one table rather than in two separate ones.
 */
interface IAccessRow {
  id: string;
  name: React.ReactNode;
  email: string;
  statusLabel: string;
  statusColor: PillColor;
  invite: CustomerUserInvite | null;
}

export const CustomerPortalUsersPage = () => {
  const { customer, refreshData } = useCustomerPortalCustomer();
  const { i18n } = useTranslation();

  const tableHeaders: ITableHeader[] = [
    { id: 'name', name: i18n('customerPortal.users.name'), width: '340px' },
    { id: 'email', name: i18n('customerPortal.users.email') },
    { id: 'status', name: i18n('customerPortal.users.isActive'), width: '170px' },
    { id: 'actions', name: '', width: '60px', align: 'right' },
  ];

  const rows: IAccessRow[] = [
    ...customer.users.map(({ user }) => ({
      id: `user-${user.id}`,
      name: user.name,
      email: user.email,
      statusLabel: user.isActive ? i18n('customerPortal.users.active') : i18n('customerPortal.users.inactive'),
      statusColor: (user.isActive ? 'positive' : 'negative') as PillColor,
      invite: null,
    })),
    ...customer.invitedUsers.map((invite) => ({
      id: `invite-${invite.id}`,
      name: <span className="font-normal text-fg-muted">—</span>,
      email: invite.email,
      statusLabel: i18n('customerPortal.users.invited'),
      statusColor: 'warning' as PillColor,
      invite,
    })),
  ];

  const meta = [
    `${i18n('customerPortal.users.users')}: ${customer.users.length}`,
    customer.invitedUsers.length
      ? `${i18n('customerPortal.users.invitedUsers')}: ${customer.invitedUsers.length}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div>
      <PageHeader title={i18n('customerPortal.users.users')} />

      <PageHeading noMarginBottom leftSide={<Breadcrumb currentItem={i18n('customerPortal.users.users')} />} />

      <div className="grid gap-4 p-7">
        <Card
          title={
            <span className="flex items-baseline gap-2.5">
              {i18n('customerPortal.users.users')}
              <span className="card-subtle-text font-normal">{meta}</span>
            </span>
          }
          flush
        >
          <Table
            idKey="id"
            headers={tableHeaders}
            data={rows}
            emptyState={<EmptyState icon={<UsersIcon />} title={i18n('customerPortal.users.noUsersFound')} />}
            mapData={(row) => [
              <UserIdentityCell name={row.name} />,
              row.email,
              <Pill color={row.statusColor}>{row.statusLabel}</Pill>,
              row.invite ? (
                <ConfirmDialog
                  triggerColor="ghost"
                  triggerText={<TrashIcon className="button-icon" />}
                  title={i18n('customerPortal.users.removeInvite')}
                  submitText={i18n('customerPortal.users.removeInvite')}
                  description={i18n('customerPortal.users.confirmRemoveInvite', {
                    email: row.invite.email,
                  })}
                  onSubmit={async () => {
                    try {
                      await deleteCustomerUserInvite(customer.id, row.invite!.id);
                      refreshData();
                      toast.success(i18n('customerPortal.users.inviteRemoved'));
                    } catch (err) {
                      captureException(err);
                      toast.error(
                        i18n('customerPortal.users.couldNotRemoveInvite', {
                          error: getDisplayError(err),
                        }),
                      );
                    }
                  }}
                />
              ) : null,
            ]}
          />
        </Card>
      </div>
    </div>
  );
};
